import { Translated } from '../../types/Global'
import { Document } from '@contentful/rich-text-types'
import { CategoryPageSection, ProductGridItemType, ProductGridType, ShortProductTileV2 } from '../../types/TemplateConfiguration'
import PageDataUtil from '../pageDataUtil/pageDataUtil'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import { MSXAttibuteValue } from '../../types/Msx'
import { ShortMsxMpv } from '../../types/ShortMsxMpv'
import { CalloutProperties, CalloutSkin, CalloutTagType, CalloutVariant } from '../../types/Callout'
import { ReviewsResponse } from '../../types/Contentful/ContentfulTypes'
import { createModelFromAutomatedExperience } from '../../components/AutoGenerate/automatedProductLaneSectionCreator'
import { AutomatedExperience } from '../../components/AutoGenerate/types/AutomatedExperience'
import { ProductLaneModel } from '../../components/AutoGenerate/types/ProductLaneModel'
import { ProductTile } from '../../components/AutoGenerate/types/ProductTile'

export interface ProductTileCollectionModel {
  title?: Translated
  subtitle?: Translated
  tiles: ProductTileModel[]
}

export interface ProductTileModel {
  name?: string
  description?: string
  image?: {
    altTextOverride?: string
    url?: string
  }
  callout?: string
  calloutColor?: CalloutSkin
  calloutVariant?: CalloutVariant
  productTileLongDescription?: Document
  linkUrl?: string
  isOutOfStock?: boolean
  externallyMarketable?: boolean
  mpvId?: string
  productId?: string
  showPrice?: boolean
  associatedPageAvailable?: boolean
  colorInformation?: MSXAttibuteValue[]
  product?: { contentful_id: string; template?: ProductTileV2 }
  version?: number
}

export interface CalloutDataType {
  skin?: CalloutSkin
  variant?: CalloutVariant
  text?: {
    value?: string
  }
  nonLocalizedValue?: CalloutTagType
}

export interface AllAvailableTags {
  default: CalloutProperties | null
  below_tile: CalloutProperties | null
}

export interface ProductTileV2 {
  calloutDetails?: CalloutDataType
  productKey?: string
  productsList?: {
    selectedProduct?: {
      mpvId?: string
    }
    selectedProductVariants?: Record<string, string>
  }
  category?: {
    selectedCategory?: {
      mpvId: string
      name: {
        value: string
      }
      link?: {
        href?: string
      }
    }
  }
  secondaryLink: {
    link?: { href?: string; text?: string }
    label?: {
      value?: string
    }
  }
  tileDetails?: ProductTileV2TileDetails
  allAvailableTags?: AllAvailableTags
}

interface ProductTileV2TileDetails {
  image?: {
    cloudinaryImage?: {
      src?: string
      altTextOverride?: string
    }[]
  }
  description?: { value?: string }
  formattedDescription?: { value?: string }
  header?: {
    value?: string
  }
  urlText?: { value?: string }
  link?: {
    href?: string
    text?: string
    linkType?: string
  }
}

export default class ProductTileCollectionUtil {
  public static getModel(section: ProductGridType, pageDataFromBFF: BFFPageData): ProductTileCollectionModel {
    const title = section.title
    const subtitle = section.subtitle

    const tiles: ProductTileModel[] = section.items.map((item) => {
      if ((item.product as ShortProductTileV2)?.template) {
        const mpvId = (item.product as ShortProductTileV2)?.template?.productsList?.selectedProduct?.mpvId
        if (mpvId) {
          const serverSideMpvData = PageDataUtil.getMpv(mpvId, pageDataFromBFF)
          const externallyMarketable = serverSideMpvData?.externallyMarketable ?? true
          const mpvTile = this.getTileV2FromMpv(mpvId, serverSideMpvData)

          let associatedPageAvailable = pageDataFromBFF.pageAvailabilities.find((pageAvailability) => pageAvailability.mpvId === mpvId)?.availability

          if ((item.product as ShortProductTileV2)?.template.tileDetails?.link?.linkType) {
            // When link url is explicitly set in authoring, do not check associated page availability
            associatedPageAvailable = true
          }
          return {
            externallyMarketable,
            ...mpvTile,
            ...item,
            mpvId,
            associatedPageAvailable,
          }
        } else {
          return {
            ...item,
            externallyMarketable: true,
            associatedPageAvailable: true,
          }
        }
      }

      return [] // map has to return a value..need to check if retuning null is an option
    })

    return {
      title,
      subtitle,
      tiles,
    }
  }

  public static getTileV2FromMpv(mpvId: string, serverSideMpvData: ShortMsxMpv | undefined) {
    const tile: ProductTileModel = {
      mpvId,
      showPrice: true,
      productId: serverSideMpvData?.coreProductId,
      isOutOfStock: serverSideMpvData?.pastaOutOfStock ?? false,
      externallyMarketable: serverSideMpvData?.externallyMarketable ?? true,
      colorInformation: serverSideMpvData?.colorInformation,
      version: serverSideMpvData?.version,
    }

    return this.removeUndefinedFields(tile)
  }

  public static getMaxColorSwatchCountDesktop(tilesCount: number, sideBarEnabled: boolean) {
    return tilesCount > 4 || sideBarEnabled ? 6 : 8
  }

  public static getNumberOfColorsToDisplay(screenClass: string, colorSwatchCount: number) {
    return screenClass === 'xs' ? 4 : colorSwatchCount
  }

  private static removeUndefinedFields(obj: any) {
    Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key])
    return obj
  }

  public static getNumberOfProductTilesPerRow(sidebarEnabled: boolean, sectionType: string, tilesCount: number) {
    switch (sectionType) {
      case 'product grid horizontal':
        return 3
      case 'product grid horizontal XL':
        return 2
      default:
        return tilesCount <= 4 ? 4 : sidebarEnabled ? 4 : 6
    }
  }

  public static getFormattedDescription(hideProductDescription: boolean | undefined, formattedDescription: string | undefined): string {
    return hideProductDescription ? '' : (formattedDescription ?? '')
  }

  public static createProductLaneModel(
    section: ProductGridType,
    sectionNumber: number,
    nextSection: CategoryPageSection | undefined,
    reviewsData: ReviewsResponse | undefined,
    enableCampaignCallouts: boolean | undefined,
    pageDataFromBFF: BFFPageData,
    automatedExperience?: AutomatedExperience,
  ): ProductLaneModel {
    const referenceSectionIds = section.sectionId
    const matchingSections = automatedExperience?.sections?.filter((section) => section.sectionId === referenceSectionIds)

    if (matchingSections && matchingSections.length > 0) {
      const automationModel = createModelFromAutomatedExperience(matchingSections[0]!, sectionNumber, nextSection)
      return automationModel
    }

    return ProductTileCollectionUtil.createModelFromManuallyGeneratedExperience(
      section,
      pageDataFromBFF,
      reviewsData,
      enableCampaignCallouts,
      sectionNumber,
      nextSection,
    )
  }

  private static createModelFromManuallyGeneratedExperience(
    section: ProductGridType,
    pageDataFromBFF: BFFPageData,
    reviewsData: ReviewsResponse | undefined,
    enableCampaignCallouts: boolean | undefined,
    sectionNumber: number,
    nextSection: CategoryPageSection | undefined,
  ) {
    const tiles: ProductTile[] = []
    section.items.forEach((item: ProductGridItemType) => {
      const tileData = (item.product as ShortProductTileV2)?.template
      if (tileData) {
        const mpvId = tileData.productsList?.selectedProduct?.mpvId

        const commonTileFields = {
          contentful_id: item.product?.contentful_id,
          name: tileData.tileDetails?.header?.value,
          description: tileData.tileDetails?.description?.value,
          formattedDescription: tileData.tileDetails?.formattedDescription?.value,
          image: tileData.tileDetails?.image?.cloudinaryImage?.[0],
          linkUrl: tileData.tileDetails?.link?.href,
          callout: tileData.calloutDetails,
          allAvailableTags: tileData.allAvailableTags,
          enableCampaignCallouts,
          selectedProductVariants: tileData?.productsList?.selectedProductVariants,
        }

        if (mpvId) {
          const serverSideMpvData = PageDataUtil.getMpv(mpvId, pageDataFromBFF)
          const mpvTile = this.getTileV2FromMpv(mpvId, serverSideMpvData)
          let associatedPageAvailable = pageDataFromBFF.pageAvailabilities.find((pageAvailability: any) => pageAvailability.mpvId === mpvId)?.availability

          if (tileData.tileDetails?.link?.linkType) {
            associatedPageAvailable = true
          }

          tiles.push({
            ...commonTileFields,
            associatedPageAvailable,
            review: reviewsData?.[mpvId],
            externallyMarketable: mpvTile.externallyMarketable,
            mpvId: mpvTile.mpvId,
            productId: mpvTile.productId,
            showPrice: mpvTile.showPrice,
            colorInformation: mpvTile.colorInformation,
            version: mpvTile.version,
            isOutOfStock: mpvTile.isOutOfStock,
          })
        } else {
          tiles.push({
            ...commonTileFields,
            externallyMarketable: true,
            associatedPageAvailable: true,
          })
        }
      }
    })

    return {
      title: section.title?.value,
      sectionNumber,
      type: section.type,
      sectionId: section.sectionId,
      nextSectionId: nextSection?.nextSectionId,
      nextSectionTitle: nextSection?.nextSectionTitle,
      subtitle: section?.subtitle?.value,
      hideProductDescription: section.hideProductDescription,
      enableSeeAllCTA: section.enableSeeAllCTA,
      seeAllCta: section.seeAllCta,
      tiles: tiles,
      isAutomatedLane: false,
    }
  }
}
